// Implements feature flags for the app using LaunchDarkly.
//
// Remember to create the flag in LaunchDarkly first:
// https://app.launchdarkly.com/broadly/production/features
//
// Usage:
//
// import { useFlags } from '../app/feature_flags';
//
// function MyComponent() {
//   const flags = useFlags();
//
//   return (
//     <>
//       {flags.appFooBarFeature ? 'On!' : 'Off'}
//     </>
//   );
// }
//

import * as Business      from '../business';
import * as User          from '../user';
import { useFlags }       from 'launchdarkly-react-client-sdk';
import { useLDClient  }   from 'launchdarkly-react-client-sdk';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import React              from 'react';
import useRouteParams     from '../hooks/use_route_params';


const clientSideID = '613a28fa826cc1254d9b8495';


function FeatureFlags({ children }) {
  return (
    <>
      <Identifier/>
      {children}
    </>
  );
}


const isBroadly = /@broadly\.com$/;

function Identifier() {
  const client         = useLDClient();
  const { businessID } = useRouteParams();
  const business       = Business.useBusiness(businessID);
  const user           = User.useProfile();
  const isSalesforce   = User.useIsSalesforce();
  const canIdentify    = (client && user && business);

  if (canIdentify) {
    if (!isSalesforce && isBroadly.test(user.email))
      return <UserIdentifier {...{ client, user }}/>;
    else
      return <BusinessIdentifier {...{ client, business }}/>;
  } else
    return null;
}


function UserIdentifier({ client, user }) {
  const { userID, name, email } = user;

  React.useEffect(function initializeLDClient() {
    const identifier = {
      key:    `user:${email}`,
      name,
      custom: {
        userID,
        email,
      },
    };

    client.identify(identifier);
  }, [ client, userID, name, email ]);

  return null;
}


function BusinessIdentifier({ client, business }) {
  const { id, name, status, industry } = business;

  React.useEffect(function initializeLDClient() {
    const identifier = {
      key:    `business:${id}`,
      name,
      custom: {
        businessID:       id,
        businessStatus:   status,
        businessIndustry: industry,
      },
    };

    client.identify(identifier);
  }, [ client, id, name, status, industry ]);

  return null;
}


const emptyFlags = {};

function useInternalFlags() {
  const flags          = useFlags();
  const { businessID } = useRouteParams();
  const business       = Business.useBusiness(businessID);

  // Reset to the undefined state when the businessID changes
  if (businessID && !business)
    return emptyFlags;
  else
    return flags;
}

export default withLDProvider({
  clientSideID,

  options: {
    // Flags may affect the UI significantly. We don't have a use case for
    // turning flags on and off in real time, and doing so may cause a lot of
    // confusion to users.
    streaming: false,
  },
})(FeatureFlags);

export { useInternalFlags as useFlags };
